/**
 * nu-header-scroll.js — rhode-style hide-on-scroll for the floating menu.
 *
 * Sets data-nu-scroll="top" | "up" | "down" on the sticky `.header-section`
 * wrapper; brand.css slides `#header-component` up/down from that attribute
 * (hover / focus / open submenus keep it visible via CSS).
 *
 * Why not Horizon's own data-scroll-direction: header.js decides "at top"
 * from getBoundingClientRect().top >= 0, which is always true once the
 * floating frame sticks the header at top: var(--frame-gap-sm).
 *
 * Uses Horizon's scroll-container helpers so the desktop (.page-wrapper)
 * vs mobile (document) scroller switch at 990px is handled for us.
 */
import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/** px of travel before a direction change counts — filters trackpad jitter */
const HYSTERESIS = 8;

let lastY = 0;
let target = null;
let ticking = false;

/** @returns {HTMLElement | null} */
const getSection = () => document.querySelector('.header-section');

/**
 * The wrapper's in-flow top: the bottom edge of whatever sits above it
 * (the announcement bar). Below this the header is stuck and can hide.
 * @param {HTMLElement} section
 */
const naturalTop = (section) => {
  const prev = /** @type {HTMLElement | null} */ (section.previousElementSibling);
  return prev ? prev.offsetTop + prev.offsetHeight : 0;
};

const update = () => {
  ticking = false;
  const section = getSection();
  if (!section) return;

  const y = Math.max(0, getScrollTop());
  const delta = y - lastY;
  const top = naturalTop(section);

  if (y <= top) {
    section.dataset.nuScroll = 'top';
    lastY = y;
    return;
  }

  if (Math.abs(delta) < HYSTERESIS) return;

  // Only hide once the user has scrolled a full header height past the
  // point where it sticks, so it never snaps away the moment it lands.
  if (delta > 0 && y < top + section.offsetHeight) {
    lastY = y;
    return;
  }

  section.dataset.nuScroll = delta > 0 ? 'down' : 'up';
  lastY = y;
};

const onScroll = () => {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(update);
};

const bind = () => {
  target?.removeEventListener('scroll', onScroll);
  target = getScrollEventTarget();
  target.addEventListener('scroll', onScroll, { passive: true });
  lastY = getScrollTop();
  update();
};

bind();
scrollContainerMediaQuery.addEventListener('change', bind);
document.addEventListener('shopify:section:load', bind);
